/**
 * CardSearchFilters - Collapsible type / color / mana value filters for `CardSearchPanel`
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoChevronDown } from 'react-icons/io5';

import type { Color } from '@/types/card';

import {
  CARD_SEARCH_CMC_OPTIONS,
  CARD_SEARCH_COLORS,
  CARD_SEARCH_TYPES,
  EMPTY_CARD_SEARCH_FILTERS,
  hasActiveCardSearchFilters,
  type CardSearchFilterState,
  type CardSearchType,
} from './cardSearchFiltering';

export interface CardSearchFiltersProps {
  filters: CardSearchFilterState;
  onChange: (filters: CardSearchFilterState) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export default function CardSearchFilters({ filters, onChange, isExpanded, onToggleExpanded }: CardSearchFiltersProps): ReactElement {
  const hasActive = hasActiveCardSearchFilters(filters);

  const toggleType = (type: CardSearchType) => {
    const types = new Set(filters.types);
    types.has(type) ? types.delete(type) : types.add(type);
    onChange({ ...filters, types });
  };

  const toggleColor = (color: Color) => {
    const colors = new Set(filters.colors);
    colors.has(color) ? colors.delete(color) : colors.add(color);
    onChange({ ...filters, colors });
  };

  const toggleCmc = (cmc: number) => {
    const isSelected = filters.cmcMin === cmc && filters.cmcMax === cmc;
    if (isSelected) {
      onChange({ ...filters, cmcMin: null, cmcMax: null });
      return;
    }
    // 7 is an open-ended "7+" bucket, so it sets no upper bound.
    onChange({ ...filters, cmcMin: cmc, cmcMax: cmc === 7 ? null : cmc });
  };

  return (
    <>
      <button
        type="button"
        onClick={onToggleExpanded}
        className={clsx(
          'text-ash hover:text-silver flex w-full items-center justify-between text-sm transition-colors',
          hasActive && 'text-aether',
        )}
        aria-expanded={isExpanded}
      >
        <span className="flex items-center gap-2">
          Filters
          {hasActive && <span className="bg-aether/20 text-aether rounded-full px-2 py-0.5 text-xs">Active</span>}
        </span>
        <IoChevronDown className={clsx('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="space-y-3 pt-2">
          <div>
            <span className="text-ash mb-2 block text-xs font-medium tracking-wide uppercase">Card Type</span>
            <div className="flex flex-wrap gap-1.5">
              {CARD_SEARCH_TYPES.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleType(value)}
                  aria-pressed={filters.types.has(value)}
                  className={clsx(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    filters.types.has(value) ? 'bg-aether text-white' : 'bg-slate text-silver hover:bg-storm',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-ash mb-2 block text-xs font-medium tracking-wide uppercase">Colors</span>
            <div className="flex items-center gap-2">
              {CARD_SEARCH_COLORS.map(({ value, label, symbol }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleColor(value)}
                  title={label}
                  className={clsx(
                    'flex h-7 w-7 items-center justify-center rounded-full transition-all',
                    filters.colors.has(value) ? 'ring-aether scale-110 ring-2' : 'opacity-60 hover:opacity-100',
                  )}
                  aria-label={`Filter by ${label}`}
                  aria-pressed={filters.colors.has(value)}
                >
                  <i className={clsx('ms', `ms-${symbol}`, 'ms-cost text-base')} aria-hidden="true" />
                </button>
              ))}
              <button
                type="button"
                onClick={() => onChange({ ...filters, includeColorless: !filters.includeColorless })}
                title="Colorless"
                className={clsx(
                  'flex h-7 w-7 items-center justify-center rounded-full transition-all',
                  filters.includeColorless ? 'ring-aether scale-110 ring-2' : 'opacity-60 hover:opacity-100',
                )}
                aria-label="Filter by Colorless"
                aria-pressed={filters.includeColorless}
              >
                <i className="ms ms-c ms-cost text-base" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div>
            <span className="text-ash mb-2 block text-xs font-medium tracking-wide uppercase">Mana Value (CMC)</span>
            <div className="flex flex-wrap gap-1.5">
              {CARD_SEARCH_CMC_OPTIONS.map((cmc) => (
                <button
                  key={cmc}
                  type="button"
                  onClick={() => toggleCmc(cmc)}
                  aria-pressed={filters.cmcMin === cmc}
                  className={clsx(
                    'flex h-7 w-7 items-center justify-center rounded-md text-xs font-medium transition-colors',
                    filters.cmcMin === cmc ? 'bg-aether text-white' : 'bg-slate text-silver hover:bg-storm',
                  )}
                >
                  {cmc === 7 ? '7+' : cmc}
                </button>
              ))}
            </div>
          </div>

          {hasActive && (
            <button
              type="button"
              onClick={() => onChange(EMPTY_CARD_SEARCH_FILTERS)}
              className="text-ash hover:text-danger text-xs transition-colors"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}
    </>
  );
}
